import { Link } from "react-router-dom";
import { Moon, Lock, Snowflake, Clock, Carrot, Soup, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { isPaid } from "@/lib/planActions";
import { useTomorrow } from "@/lib/reminders";

const TIP_ICON = { prep_overnight: Moon, defrost_fridge: Snowflake, prep_veg: Carrot, long_cook: Clock, soak: Soup };

// Dashboard card: tomorrow's meals + what to take out / prep tonight (Plus/Premium)
export function TomorrowCard() {
  const { t, tv } = useLang();
  const { user } = useAuth();
  const paid = isPaid(user);
  const { data } = useTomorrow(paid);

  return (
    <div className="chenia-card p-6" data-testid="tomorrow-card">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-chenia-terracottadark font-semibold"><Moon className="h-4 w-4" /> {t("tomorrow_title")}</div>
        {paid && <Link to="/settings" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1" data-testid="tomorrow-settings">{t("reminders")} <ChevronRight className="h-3 w-3" /></Link>}
      </div>
      <p className="mt-1 text-sm text-muted-foreground">{t("tomorrow_sub")}</p>

      {!paid ? (
        <div className="mt-4 rounded-2xl bg-chenia-rosesoft/50 p-4 flex flex-wrap items-center gap-3" data-testid="tomorrow-locked">
          <Lock className="h-4 w-4 text-primary" />
          <p className="text-sm flex-1 min-w-40">{t("premium_feature")}</p>
          <Button asChild size="sm" className="rounded-full bg-primary hover:bg-chenia-terracottadark"><Link to="/pricing" data-testid="tomorrow-unlock">{t("unlock")}</Link></Button>
        </div>
      ) : !data ? null : data.meals.length === 0 ? (
        <p className="mt-4 text-sm text-muted-foreground" data-testid="tomorrow-empty">{t("no_tomorrow")}</p>
      ) : (
        <div className="mt-4 space-y-2" data-testid="tomorrow-meals">
          {data.meals.map((m) => (
            <Link to={`/recipe/${m.recipe_id}`} key={m.slot} className="flex gap-3 items-start rounded-2xl p-2 hover:bg-secondary transition-colors" data-testid={`tomorrow-meal-${m.slot}`}>
              <img src={m.image} alt="" className="h-12 w-12 rounded-xl object-cover shrink-0" style={{ objectPosition: m.focal || "50% 50%" }} />
              <div className="min-w-0 flex-1">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">{t(m.slot)} · {m.total_time} {t("min")}</p>
                <p className="font-medium truncate">{tv(m.name)}</p>
                {m.tips.length > 0 && (
                  <ul className="mt-1 space-y-0.5">
                    {m.tips.map((c) => { const Icon = TIP_ICON[c] || Moon; return (
                      <li key={c} className="text-xs text-chenia-terracottadark inline-flex items-center gap-1.5 mr-3" data-testid={`tip-${m.slot}-${c}`}><Icon className="h-3 w-3" /> {t(`tip_${c}`)}</li>
                    ); })}
                  </ul>
                )}
              </div>
            </Link>
          ))}
          {data.meals.every((m) => m.tips.length === 0) && <p className="text-xs text-muted-foreground px-2" data-testid="tomorrow-all-set">{t("all_set")}</p>}
        </div>
      )}
    </div>
  );
}
